import { IApiResult, IDropdownDto } from '../generated-types/api-result'
import {
  ICustomerFormSchema,
  IFormSchema,
  IFormSchemaColumn,
  IFormSchemaDto,
  IFormSchemaReport,
  IFormSchemaVersion,
  IMoveFormSchemaReportDto,
  SchemaType,
  formSchemaReportTypes,
} from '../generated-types/form-schema'
import { queryClient } from '../query-client'
import { formSchemaQueryKeys } from '../query-keys'
import { useDelete, useGet, usePost, usePut } from './reactQuery'

const baseUrl = (customerId: EntityId) => `customers/${customerId}/form-schema`
export const endpoints = {
  getFormSchemas(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  getFormAllSchemas(customerId: EntityId) {
    return `${baseUrl(customerId)}/all`
  },
  getFormSchemasDropdown(customerId: EntityId) {
    return `${baseUrl(customerId)}/dropdown`
  },
  getFormSchemaById(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}`
  },
  getFormSchemaWithReports(customerId: EntityId) {
    return `${baseUrl(customerId)}/form-schema-reports`
  },
  getChecklistStatus(customerId: EntityId) {
    return `${baseUrl(customerId)}/checklist-status`
  },
  deleteFormSchemaById(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  createFormSchema(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  createFormSchemaReport(customerId: EntityId) {
    return `${baseUrl(customerId)}/form-schema-report`
  },
  updateFormSchemaReport(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/form-schema-report/${id}`
  },
  publishFormSchemaReport(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/form-schema-report/${id}/publish`
  },
  moveFormSchemaReport(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/form-schema-report/${id}/move`
  },
  deleteFormSchemaReportById(customerId: EntityId) {
    return `${baseUrl(customerId)}/form-schema-report`
  },
  updateFormSchema(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}`
  },
  generateBulkExcelExportF(customerId: EntityId) {
    return `${baseUrl(customerId)}/excel-bulk`
  },
  getFilledFormSchemaReports(customerId: EntityId) {
    return `${baseUrl(customerId)}/filled-reports`
  },
  getFilledFormSchemaReportsFilterOptions(customerId: EntityId) {
    return `${baseUrl(customerId)}/filled-reports/filter-options`
  },
  getFilledFormSchemaReportValues(customerId: EntityId, reportId: number) {
    return `${baseUrl(customerId)}/filled-reports/${reportId}/values`
  },
  getFilledFormSchemaReportWithVersion(customerId: EntityId, reportId: number) {
    return `${baseUrl(customerId)}/filled-reports/${reportId}/with-version`
  },
  getFormSchemasForMovement(customerId: EntityId) {
    return `${baseUrl(customerId)}/for-movement`
  },
  getFormSchemaColumns(customerId: EntityId, id: number) {
    return `${baseUrl(customerId)}/${id}/columns`
  },
}
export const invalidateSchemaForm = {
  getFormSchemaReports(
    customerId?: EntityId,
    type?: formSchemaReportTypes,
    localityFrequencyIds?: Array<number | undefined>,
    deviationId?: string,
    mainComponentId?: string,
    scheduledTaskId?: number,
    localityId?: number,
  ) {
    queryClient.invalidateQueries(
      formSchemaQueryKeys.GetFormSchemaReports({
        customerId,
        type,
        localityFrequencyIds,
        deviationId,
        mainComponentId,
        scheduledTaskId,
        localityId,
      }),
    )
  },
  getSchemaById(customerId: EntityId, id: EntityId) {
    queryClient.invalidateQueries([endpoints.getFormSchemaById(customerId, id)])
  },
  getAllFormSchema(customerId: EntityId) {
    queryClient.invalidateQueries([endpoints.getFormAllSchemas(customerId)])
    queryClient.invalidateQueries([endpoints.getFormSchemasDropdown(customerId)])
  },
  getFilledFormSchemaReports(customerId: EntityId, queryParams?: { localityId?: number }) {
    queryClient.invalidateQueries(formSchemaQueryKeys.GET_FILLED_FORM_SCHEMA_REPORTS(customerId, queryParams ?? {}))
  },
  getFormSchemasForMovement(customerId: EntityId, equipmentTypeIds?: number[], movementTypes?: number[]) {
    queryClient.invalidateQueries(
      formSchemaQueryKeys.GET_FORM_SCHEMAS_FOR_MOVEMENT(customerId, {
        equipmentTypeIds: equipmentTypeIds ?? [],
        movementTypes: movementTypes ?? [],
      }),
    )
  },
  useGetFormSchemaColumns(customerId: EntityId, id?: number) {
    queryClient.invalidateQueries(formSchemaQueryKeys.GET_FORM_SCHEMA_COLUMNS(customerId, id))
  },
  getChecklistStatus(customerId: EntityId, scheduledTaskId?: number) {
    queryClient.invalidateQueries(formSchemaQueryKeys.GET_CHECKLIST_STATUS(customerId, scheduledTaskId))
  },
}

export const useGetFormSchema = (customerId: EntityId, queryParams?: { schemaType?: SchemaType }) =>
  useGet<ICustomerFormSchema[]>(endpoints.getFormSchemas(customerId), queryParams)

export const useGetAllFormSchema = (customerId: EntityId, queryParams: { schemaType: SchemaType }) =>
  useGet<ICustomerFormSchema[]>(endpoints.getFormAllSchemas(customerId), queryParams)

export const useGetFormSchemasDropdown = (customerId: EntityId, schemaType?: SchemaType) =>
  useGet<IDropdownDto[]>(endpoints.getFormSchemasDropdown(customerId), { schemaType: schemaType })

export const useGetFormSchemaById = (customerId: EntityId, id: EntityId) =>
  useGet<IFormSchemaVersion>(endpoints.getFormSchemaById(customerId, id))

export const useGetFormSchemaReports = (
  customerId: EntityId,
  queryParams: {
    type: formSchemaReportTypes
    localityFrequencyId?: number
    groupId?: string
    deviationId?: string
    mainComponentId?: string
    scheduledTaskId?: number
    localityId?: number
    schemaType?: SchemaType
    activityLogId?: EntityId
  },
) =>
  useGet<IFormSchemaDto[]>(endpoints.getFormSchemaWithReports(customerId), queryParams, {
    queryKey: formSchemaQueryKeys.GetFormSchemaReports({
      customerId,
      ...queryParams,
    }),
  })

export const useGetChecklistStatus = (customerId: EntityId, scheduledTaskId?: number) =>
  useGet<boolean>(
    endpoints.getChecklistStatus(customerId),
    { scheduledTaskId },
    {
      enabled: scheduledTaskId != null,
      queryKey: formSchemaQueryKeys.GET_CHECKLIST_STATUS(customerId, scheduledTaskId),
    },
  )

export const useDeleteFormSchemaById = (customerId: EntityId) =>
  useDelete<IFormSchemaVersion>(endpoints.deleteFormSchemaById(customerId))

export const useAddFormSchema = (customerId: EntityId) =>
  usePost<IFormSchema, IFormSchema>(endpoints.createFormSchema(customerId))

export const useAddFormSchemaReport = (customerId: EntityId) =>
  usePost<IFormSchemaReport, IFormSchemaReport>(endpoints.createFormSchemaReport(customerId))

export const useUpdateFormSchemaReport = (customerId: EntityId, id: EntityId) =>
  usePut<IFormSchemaReport, { values?: string; draftCaption?: string }>(
    endpoints.updateFormSchemaReport(customerId, id),
  )

export const usePublishFormSchemaReport = (customerId: EntityId, id: EntityId) =>
  usePost<IFormSchemaReport, object>(endpoints.publishFormSchemaReport(customerId, id))

export const useMoveFormSchemaReport = (customerId: EntityId, id: EntityId) =>
  usePost<IApiResult, IMoveFormSchemaReportDto>(endpoints.moveFormSchemaReport(customerId, id))

export const useDeleteFormSchemaReportById = (customerId: EntityId) =>
  useDelete<IApiResult>(endpoints.deleteFormSchemaReportById(customerId))

export const useUpdateFormSchema = (customerId: EntityId, id: EntityId) =>
  usePut<IFormSchema, IFormSchema>(endpoints.updateFormSchema(customerId, id))

export const useBulkExcelExport = (customerId: EntityId) =>
  usePost<any, object>(endpoints.generateBulkExcelExportF(customerId), {}, { responseType: 'arraybuffer' })

export const useGetFormSchemasForMovement = (
  customerId: EntityId,
  params: { equipmentTypeIds: number[]; movementTypes: number[] },
) =>
  useGet<ICustomerFormSchema[]>(
    endpoints.getFormSchemasForMovement(customerId),
    {
      equipmentTypeIds: params.equipmentTypeIds.join(','),
      movementTypes: params.movementTypes.join(','),
    },
    {
      queryKey: formSchemaQueryKeys.GET_FORM_SCHEMAS_FOR_MOVEMENT(customerId, {
        equipmentTypeIds: params.equipmentTypeIds,
        movementTypes: params.movementTypes,
      }),
    },
  )

export const useGetFormSchemaColumns = (customerId: EntityId, id: number) =>
  useGet<IFormSchemaColumn[]>(endpoints.getFormSchemaColumns(customerId, id), undefined, {
    queryKey: formSchemaQueryKeys.GET_FORM_SCHEMA_COLUMNS(customerId, id),
    enabled: !!id,
  })
